import React from 'react';
import { Helmet } from 'react-helmet-async';
import { FiPhone, FiMail, FiMapPin, FiClock, FiMessageCircle } from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import { useReservation } from '../contexts/ReservationContext';

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    { "@type": "ListItem", "position": 1, "name": "Accueil", "item": "https://limousine-vip-paris.fr/" },
    { "@type": "ListItem", "position": 2, "name": "Contact", "item": "https://limousine-vip-paris.fr/contact" }
  ]
};

const contactSchema = {
  "@context": "https://schema.org",
  "@type": "ContactPage",
  "name": "Contact VIP Limousine Paris",
  "url": "https://limousine-vip-paris.fr/contact",
  "mainEntity": {
    "@type": "LocalBusiness",
    "@id": "https://limousine-vip-paris.fr/#business",
    "name": "VIP Limousine Paris",
    "telephone": "+33661630113",
    "email": "contact@limousine-vip-paris.fr",
    "url": "https://limousine-vip-paris.fr/",
    "openingHours": "Mo-Su 00:00-23:59",
    "areaServed": [
      "Paris", "Hauts-de-Seine", "Seine-Saint-Denis", "Val-de-Marne",
      "Yvelines", "Val-d'Oise", "Essonne", "Seine-et-Marne"
    ]
  }
};

const ContactPage = () => {
  const { open } = useReservation();
  const phoneNumber = '33661630113';
  const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent('Bonjour, je souhaite obtenir un devis pour une limousine VIP Limousine Paris.')}`;

  const cardStyle = {
    background: 'linear-gradient(135deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))',
    border: '1px solid rgba(212,175,55,0.2)',
    borderRadius: '1rem',
    padding: '1.5rem',
    backdropFilter: 'blur(20px)',
    transition: 'all 0.2s'
  };

  return (
    <>
      <Helmet>
        <title>Contact VIP Limousine Paris - Devis 24/7 ★ 06 61 63 01 13</title>
        <meta name="description" content="Contactez VIP Limousine Paris pour votre devis gratuit en moins d'une heure. Téléphone, WhatsApp, email, formulaire en ligne. Service 24h/24, 7j/7 à Paris et en Île-de-France." />
        <link rel="canonical" href="https://limousine-vip-paris.fr/contact" />
        <meta property="og:title" content="Contact VIP Limousine Paris - 06 61 63 01 13" />
        <meta property="og:description" content="Devis gratuit en moins d'une heure. Téléphone, WhatsApp, email. Service 24/7." />
        <meta property="og:url" content="https://limousine-vip-paris.fr/contact" />

        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(contactSchema)}</script>
      </Helmet>

      <div style={{ paddingTop: '120px', paddingBottom: '4rem' }}>
        <div className="container">
          <div className="section-header text-center">
            <span className="section-badge">Contactez-nous</span>
            <h1 className="section-title">
              VIP Limousine Paris
              <span className="title-accent">à votre écoute 24/7</span>
            </h1>
            <p className="section-description">
              Réponse en moins d'une heure 24h/24, 7j/7. Devis gratuit et sans engagement
              pour mariages, transferts aéroport, EVJF, anniversaires et événements VIP.
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            gap: '1rem',
            marginTop: '2rem'
          }}>
            <a href={`tel:+${phoneNumber}`} style={{ ...cardStyle, color: 'inherit', textDecoration: 'none' }}>
              <FiPhone size={28} color="var(--primary-gold)" style={{ marginBottom: '0.75rem' }} />
              <h3 style={{ marginBottom: '0.4rem', color: 'var(--text-primary)' }}>Téléphone</h3>
              <p style={{ color: 'var(--primary-gold)', fontSize: '1.15rem', fontWeight: 700, margin: 0 }}>
                06 61 63 01 13
              </p>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginTop: '0.4rem' }}>
                Réservations & urgences 24/7
              </p>
            </a>

            <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" style={{ ...cardStyle, color: 'inherit', textDecoration: 'none' }}>
              <FaWhatsapp size={28} color="#25D366" style={{ marginBottom: '0.75rem' }} />
              <h3 style={{ marginBottom: '0.4rem', color: 'var(--text-primary)' }}>WhatsApp</h3>
              <p style={{ color: 'var(--primary-gold)', fontSize: '1.05rem', fontWeight: 600, margin: 0 }}>
                Devis instantané
              </p>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginTop: '0.4rem' }}>
                Réponse en moins d'une heure
              </p>
            </a>

            <a href="mailto:contact@limousine-vip-paris.fr" style={{ ...cardStyle, color: 'inherit', textDecoration: 'none' }}>
              <FiMail size={28} color="var(--primary-gold)" style={{ marginBottom: '0.75rem' }} />
              <h3 style={{ marginBottom: '0.4rem', color: 'var(--text-primary)' }}>Email</h3>
              <p style={{ color: 'var(--primary-gold)', fontSize: '1rem', fontWeight: 600, margin: 0, wordBreak: 'break-all' }}>
                contact@limousine-vip-paris.fr
              </p>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginTop: '0.4rem' }}>
                Devis détaillés & informations
              </p>
            </a>

            <button
              type="button"
              onClick={() => open()}
              style={{ ...cardStyle, color: 'inherit', cursor: 'pointer', textAlign: 'left', font: 'inherit' }}
            >
              <FiMessageCircle size={28} color="var(--primary-gold)" style={{ marginBottom: '0.75rem' }} />
              <h3 style={{ marginBottom: '0.4rem', color: 'var(--text-primary)' }}>Formulaire en ligne</h3>
              <p style={{ color: 'var(--primary-gold)', fontSize: '1.05rem', fontWeight: 600, margin: 0 }}>
                Réservation détaillée
              </p>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginTop: '0.4rem' }}>
                Tous vos besoins en un seul envoi
              </p>
            </button>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            gap: '1rem',
            marginTop: '1rem'
          }}>
            <div style={cardStyle}>
              <FiMapPin size={28} color="var(--primary-gold)" style={{ marginBottom: '0.75rem' }} />
              <h3 style={{ marginBottom: '0.4rem', color: 'var(--text-primary)' }}>Zone d'intervention</h3>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem', margin: 0 }}>
                Paris (75) · Hauts-de-Seine (92) · Seine-Saint-Denis (93) · Val-de-Marne (94) ·
                Yvelines (78) · Val-d'Oise (95) · Essonne (91) · Seine-et-Marne (77)
              </p>
            </div>

            <div style={cardStyle}>
              <FiClock size={28} color="var(--primary-gold)" style={{ marginBottom: '0.75rem' }} />
              <h3 style={{ marginBottom: '0.4rem', color: 'var(--text-primary)' }}>Horaires</h3>
              <p style={{ color: 'var(--primary-gold)', fontSize: '1.1rem', fontWeight: 700, margin: 0 }}>
                24h/24 - 7j/7
              </p>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginTop: '0.4rem' }}>
                Y compris dimanches et jours fériés
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ContactPage;
